import { canvasPaint } from "./native-gradient";
import { type RenderContext } from "./canvas-surface";
import type { SceneNode } from "../scene/model";
import { vectorPath, arrowPoints } from "../native/vector-path";
import { shapeStyle } from "../native/appearance";
export function paintVector(ctx: RenderContext, node: SceneNode) {
  if (node.geometry.svgPath) {
    const s = node.geometry.svgPath as {
      d: string;
      x: number;
      y: number;
      width: number;
      height: number;
    };
    ctx.save();
    ctx.scale(node.geometry.width! / s.width, node.geometry.height! / s.height);
    ctx.translate(-s.x - s.width / 2, -s.y - s.height / 2);
    const style = shapeStyle(node),
      path = new Path2D(s.d);
    if (style.fillEnabled) {
      ctx.save();
      ctx.fillStyle = canvasPaint(
        ctx,
        style.fill,
        node.appearance.fillGradient,
        s.width,
        s.height,
        s.x,
        s.y,
      );
      ctx.globalAlpha *= Number(node.appearance.fillAlpha ?? 1);
      ctx.fill(path);
      ctx.restore();
    }
    if (style.strokeEnabled) {
      ctx.strokeStyle = canvasPaint(
        ctx,
        style.stroke,
        node.appearance.strokeGradient,
        s.width,
        s.height,
        s.x,
        s.y,
      );
      ctx.lineWidth =
        style.strokeWidth /
        Math.sqrt(
          (node.geometry.width! * node.geometry.height!) / (s.width * s.height),
        );
      ctx.globalAlpha *= Number(node.appearance.strokeAlpha ?? 1);
      ctx.stroke(path);
    }
    ctx.restore();
    return;
  }
  const p = vectorPath(node),
    a = p.anchors,
    w = node.geometry.width!,
    h = node.geometry.height!,
    style = shapeStyle(node);
  ctx.beginPath();
  ctx.moveTo(a[0]!.x * w, a[0]!.y * h);
  for (let i = 0; i < a.length - (p.closed ? 0 : 1); i++) {
    const start = a[i]!,
      end = a[(i + 1) % a.length]!,
      c1 = start.outgoing ?? start,
      c2 = end.incoming ?? end;
    ctx.bezierCurveTo(
      c1.x * w,
      c1.y * h,
      c2.x * w,
      c2.y * h,
      end.x * w,
      end.y * h,
    );
  }
  if (p.closed) ctx.closePath();
  if (p.closed && style.fillEnabled) {
    ctx.fillStyle = canvasPaint(
      ctx,
      style.fill,
      node.appearance.fillGradient,
      w,
      h,
    );
    ctx.save();
    ctx.globalAlpha *= Number(node.appearance.fillAlpha ?? 1);
    ctx.fill();
    ctx.restore();
  }
  if (style.strokeEnabled && style.strokeWidth > 0) {
    ctx.strokeStyle = canvasPaint(
      ctx,
      style.stroke,
      node.appearance.strokeGradient,
      w,
      h,
    );
    ctx.lineWidth = style.strokeWidth;
    ctx.lineJoin = "round";
    ctx.lineCap = style.lineCap;
    ctx.setLineDash(
      style.strokeDash ??
        (style.lineStyle === "dashed"
          ? [style.strokeWidth * 4, style.strokeWidth * 2]
          : style.lineStyle === "dotted"
            ? [0, style.strokeWidth * 2]
            : []),
    );
    ctx.save();
    ctx.globalAlpha *= Number(node.appearance.strokeAlpha ?? 1);
    ctx.stroke();
    ctx.restore();
    ctx.setLineDash([]);
    if (p.arrow) {
      const arrow = arrowPoints(node);
      ctx.beginPath();
      ctx.moveTo(arrow[0]!.x * w, arrow[0]!.y * h);
      for (const q of arrow.slice(1)) ctx.lineTo(q.x * w, q.y * h);
      ctx.closePath();
      ctx.fillStyle = ctx.strokeStyle;
      ctx.globalAlpha *= Number(node.appearance.strokeAlpha ?? 1);
      ctx.fill();
    }
  }
}
